import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchFiatDeposit, fetchFiatDeposits, formatAedAmount } from "../../../api/services/fiatService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { exportFiatDepositHistoryBrandedExcel } from "../../../utils/exportBrandedHistoryExcel";
import "../WithdrawPage/withdrawal.css";

const HISTORY_PAGE_SIZE = 20;

const TIME_FILTER_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All status" },
  { value: "COMPLETED", label: "Completed" },
  { value: "LIMIT_HOLD", label: "In review" },
  { value: "REJECTED_HOLD", label: "Not accepted" },
  { value: "REFUND_PENDING", label: "Refund pending" },
  { value: "REFUNDED", label: "Refunded" },
  { value: "REVERSED", label: "Reversed" },
  { value: "FAILED", label: "Failed" },
];

const STATUS_LABEL = {
  COMPLETED: "Completed",
  LIMIT_HOLD: "In review",
  REJECTED_HOLD: "Not accepted",
  REFUND_PENDING: "Refund pending",
  REFUNDED: "Refunded",
  REVERSED: "Reversed",
  FAILED: "Failed",
};

function statusTone(status) {
  if (status === "COMPLETED") return "success";
  if (status === "LIMIT_HOLD" || status === "REFUND_PENDING") return "pending";
  if (status === "REFUNDED") return "info";
  if (status === "REJECTED_HOLD" || status === "REVERSED" || status === "FAILED") return "danger";
  return "neutral";
}

function formatHistDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function last4Label(last4) {
  const s = String(last4 || "").trim();
  return s ? `•••• ${s}` : "—";
}

function ordinal(n) {
  const v = Number.parseInt(String(n), 10);
  if (!Number.isFinite(v) || v < 1) return "";
  const m = v % 100;
  const suffix = ["th", "st", "nd", "rd"][(m - 20) % 10] || ["th", "st", "nd", "rd"][m] || "th";
  return `${v}${suffix}`;
}

function feePositive(value) {
  const n = Number.parseFloat(String(value ?? "0"));
  return Number.isFinite(n) && n > 0;
}

function isBelowMinHold(item) {
  const status = String(item?.status || "").toUpperCase();
  if (status !== "LIMIT_HOLD") return false;
  const reason = String(item?.status_reason || "").toLowerCase();
  return /min|minimum|below/.test(reason);
}

function mapFiatDepositRow(item) {
  const status = String(item?.status || "").toUpperCase();
  const belowMinHold = isBelowMinHold(item);
  const fee = item.fee_amount || "0";
  const credited = item.net_credited || item.amount || "0";
  const hasFee = feePositive(fee);
  const gross = item.gross_amount || (!hasFee ? credited : "");
  const seq = item.deposit_seq_in_day;
  return {
    id: String(item.id || ""),
    createdAt: item.created_at || null,
    creditedAt: item.credited_at || null,
    dateLabel: formatHistDate(item.credited_at || item.created_at),
    amount: credited,
    currency: item.currency || "AED",
    sourceAmount: item.source_amount || "",
    sourceCurrency: item.source_currency || "",
    gross,
    fee,
    net: credited,
    hasFee,
    seqInDay: seq == null || seq === "" ? null : Number(seq),
    last4: item.debtor_iban_last4 || "",
    status,
    belowMinHold,
    statusLabel: belowMinHold ? "Below minimum" : STATUS_LABEL[status] || status || "—",
    statusTone: statusTone(status),
    statusReason: item.status_reason || "",
    channelRef: item.channel_ref_id || "",
    remittance: item.remittance_reference || "",
    wallet: item.wallet_type === "spot" || !item.wallet_type ? "Spot" : String(item.wallet_type),
    fxRate: item.fx_rate || "",
  };
}

function rowWithinTimeFilter(row, timeFilter) {
  if (!timeFilter || timeFilter === "all") return true;
  const iso = row.createdAt || row.creditedAt;
  if (!iso) return true;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return true;
  const days = { "7d": 7, "30d": 30, "90d": 90 }[timeFilter];
  if (!days) return true;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - days);
  return d >= cutoff;
}

async function copyText(value) {
  const text = String(value || "");
  if (!text) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

function DfFilterDropdown({ id, label, value, options, openKey, menuKey, onOpenToggle, onChange }) {
  const open = openKey === menuKey;
  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className={`df-dd${open ? " is-open" : ""}`} data-df-dropdown>
      <span className="df-dd-label" id={`${id}-lbl`}>
        {label}
      </span>
      <button
        type="button"
        id={id}
        className={`df-dd-trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-lbl`}
        onClick={(e) => {
          e.stopPropagation();
          onOpenToggle(open ? null : menuKey);
        }}
      >
        <span className="df-dd-value">{selected.label}</span>
        <svg className="df-dd-chevron" width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
          <path fill="currentColor" d="M1.4.6 6 5.2 10.6.6 12 2 6 8 0 2z" />
        </svg>
      </button>
      {open ? (
        <ul className="df-dd-menu" role="listbox" aria-labelledby={`${id}-lbl`}>
          {options.map((opt) => (
            <li key={opt.value} role="option" aria-selected={opt.value === value}>
              <button
                type="button"
                className={`df-dd-option${opt.value === value ? " is-selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  onOpenToggle(null);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function buildDepositSteps(row) {
  const received = {
    title: "Bank transfer received",
    time: formatHistDate(row.createdAt) !== "—" ? formatHistDate(row.createdAt) : row.dateLabel,
    emphasized: false,
  };
  if (row.status === "COMPLETED") {
    return [
      received,
      {
        title: "Credited to Spot",
        time: formatHistDate(row.creditedAt) !== "—" ? formatHistDate(row.creditedAt) : row.dateLabel,
        emphasized: true,
      },
    ];
  }
  if (row.status === "REFUNDED") {
    return [received, { title: "Refunded", time: row.dateLabel, emphasized: true }];
  }
  if (row.status === "REVERSED") {
    return [received, { title: "Reversed", time: row.dateLabel, emphasized: true }];
  }
  if (row.status === "FAILED") {
    return [received, { title: "Failed", time: row.dateLabel, emphasized: true }];
  }
  if (row.status === "REJECTED_HOLD") {
    return [received, { title: "Not accepted", time: row.dateLabel, emphasized: true }];
  }
  if (row.status === "REFUND_PENDING") {
    return [received, { title: "Refund pending", time: row.dateLabel, emphasized: true }];
  }
  return [received, { title: row.statusLabel || "In review", time: row.dateLabel, emphasized: true }];
}

function FiatDepositDetailModal({ open, row, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !row) return null;

  const steps = buildDepositSteps(row);
  const stepperOutcome =
    row.statusTone === "success" ? "success" : row.statusTone === "danger" ? "danger" : "pending";
  const feeValue = `${formatAedAmount(row.fee)} ${row.currency}`;
  const creditedValue = row.belowMinHold ? "Not credited" : `${formatAedAmount(row.net)} ${row.currency}`;
  const fields = [
    { label: "Status", value: row.statusLabel },
    { label: "Date", value: row.dateLabel },
    { label: "Gross (bank sent)", value: row.gross ? `${formatAedAmount(row.gross)} ${row.currency}` : "" },
    {
      label: "Fee",
      value: row.hasFee && row.seqInDay ? `${feeValue} · ${ordinal(row.seqInDay)} deposit today` : feeValue,
    },
    { label: "Credited to Spot", value: creditedValue },
    { label: "From IBAN", value: last4Label(row.last4) },
    { label: "Wallet", value: row.wallet },
    { label: "Reference", value: row.channelRef, copy: true },
    { label: "Remittance", value: row.remittance, copy: true },
  ].filter((f) => f.value && f.value !== "—");

  const note = row.belowMinHold
    ? [row.statusReason, "Wait for an admin refund. Do not retry KYC."].filter(Boolean).join(" ")
    : row.statusReason;

  return (
    <div className="withdraw_confirm_overlay withdraw_hist_detail_overlay" role="presentation" onClick={onClose}>
      <div
        className="withdraw_hist_detail_modal wf-fiat-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="df-fiat-dep-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="withdraw_hist_detail_head">
          <h2 id="df-fiat-dep-detail-title" className="withdraw_hist_detail_title">
            Deposit details
          </h2>
        </div>
        <div className="withdraw_hist_detail_body">
          <div className="withdraw_hist_stepper" aria-label="Deposit progress" data-outcome={stepperOutcome}>
            {steps.map((s, i, arr) => (
              <div key={`${row.id}-dstep-${i}`} className="withdraw_hist_step">
                <div className="withdraw_hist_step_track" aria-hidden="true">
                  <span className="withdraw_hist_step_dot" />
                  {i < arr.length - 1 ? <span className="withdraw_hist_step_line" /> : null}
                </div>
                <div className="withdraw_hist_step_text">
                  <div className={`withdraw_hist_step_title${s.emphasized ? " is-emphasized" : ""}`}>{s.title}</div>
                  <div className="withdraw_hist_step_time">{s.time}</div>
                </div>
              </div>
            ))}
          </div>
          {note ? (
            <p
              className={`withdraw_hist_step_note${
                row.statusTone === "danger" ? " withdraw_hist_step_note--danger" : ""
              }`}
            >
              {note}
            </p>
          ) : null}
          <div className="withdraw_hist_rows">
            {fields.map((f) => (
              <div key={f.label} className="withdraw_hist_row">
                <span className="withdraw_hist_row_label">{f.label}</span>
                <span className="withdraw_hist_row_value">
                  {f.copy ? (
                    <>
                      {f.value}
                      <button
                        type="button"
                        className="df-deposit-history-copy-btn"
                        onClick={async () => {
                          const ok = await copyText(f.value);
                          if (ok) alertSuccessMessage("Copied");
                        }}
                        aria-label={`Copy ${f.label}`}
                      >
                        <i className="ri-file-copy-line" aria-hidden />
                      </button>
                    </>
                  ) : (
                    f.value
                  )}
                </span>
              </div>
            ))}
          </div>
          <div className="withdraw_hist_help_links wf-fiat-detail-help">
            <Link to="/user_profile/create_tickets" className="withdraw_hist_gold_link">
              Need help? Chat with us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DepositFiatHistorySection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [timeFilter, setTimeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadPage = useCallback(async (nextPage, replace) => {
    if (replace) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }
    const res = await fetchFiatDeposits({
      page: nextPage,
      limit: HISTORY_PAGE_SIZE,
      status: statusFilter === "all" ? undefined : statusFilter,
    });
    if (!res.ok) {
      if (replace) {
        setRows([]);
        setError(res.error?.message || "Could not load deposit history.");
      }
      setHasMore(false);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    const mapped = (res.items || []).map(mapFiatDepositRow);
    setRows((prev) => (replace ? mapped : [...prev, ...mapped]));
    setPage(nextPage);
    setHasMore(!!res.meta?.has_more);
    setLoading(false);
    setLoadingMore(false);
  }, [statusFilter]);

  useEffect(() => {
    loadPage(1, true);
  }, [loadPage]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-df-dropdown]")) setOpenDropdown(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [openDropdown]);

  const filteredRows = useMemo(
    () => rows.filter((row) => rowWithinTimeFilter(row, timeFilter)),
    [rows, timeFilter]
  );

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    const res = await fetchFiatDeposit(row.id);
    if (res.ok && res.deposit) {
      setDetailRow(mapFiatDepositRow(res.deposit));
    }
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailRow(null);
  }, []);

  const handleExportExcel = useCallback(async () => {
    if (!filteredRows.length) {
      alertErrorMessage("No deposit history to export.");
      return;
    }
    try {
      const timeLabel = TIME_FILTER_OPTIONS.find((o) => o.value === timeFilter)?.label || "All time";
      const statusLabel = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label || "All status";
      await exportFiatDepositHistoryBrandedExcel(filteredRows, { timeLabel, statusLabel });
      alertSuccessMessage("Deposit history exported.");
    } catch {
      alertErrorMessage("Could not export deposit history.");
    }
  }, [filteredRows, timeFilter, statusFilter]);

  const bindRowOpen = (row) => ({
    tabIndex: 0,
    onClick: (e) => {
      if (e.target instanceof Element && e.target.closest(".df-deposit-history-copy-btn")) return;
      openDetail(row);
    },
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openDetail(row);
      }
    },
  });

  return (
    <>
      <section className="df-deposit-history-section depositfiathistory" aria-labelledby="df-deposit-history-title">
        <div className="container">
          <div className="df-deposit-history-top">
            <h2 id="df-deposit-history-title" className="df-deposit-history-title">
              Deposit History
            </h2>
            <div className="df-deposit-history-filters" role="search" aria-label="Filter deposit history">
              <DfFilterDropdown
                id="df-hist-time"
                label="Time"
                value={timeFilter}
                options={TIME_FILTER_OPTIONS}
                menuKey="time"
                openKey={openDropdown}
                onOpenToggle={setOpenDropdown}
                onChange={setTimeFilter}
              />
              <DfFilterDropdown
                id="df-hist-status"
                label="Status"
                value={statusFilter}
                options={STATUS_FILTER_OPTIONS}
                menuKey="status"
                openKey={openDropdown}
                onOpenToggle={setOpenDropdown}
                onChange={setStatusFilter}
              />
              <button
                type="button"
                className="df-deposit-history-export-btn"
                onClick={handleExportExcel}
                disabled={loading || filteredRows.length === 0}
              >
                Export Report
              </button>
            </div>
          </div>

          <div className="df-deposit-history-card">
            <div className="df-deposit-history-table-wrap" role="region" aria-label="Deposit history table">
              {error ? (
                <div className="df-deposit-history-status" role="status">
                  <p>{error}</p>
                  <button type="button" className="df-deposit-history-export-btn" onClick={() => loadPage(1, true)}>
                    Try again
                  </button>
                </div>
              ) : null}
              {loading && rows.length === 0 && !error ? (
                <div className="df-sk-table-wrap" aria-label="Loading deposit history">
                  {Array.from({ length: 6 }, (_, i) => (
                    <div className="df-sk-row" key={i}>
                      <div className="df-sk-coin">
                        <span className="df-sk-circle df-sk-glass" />
                        <div className="df-sk-coin-txt">
                          <span className="df-sk-glass df-sk-bone--xs" />
                          <span className="df-sk-glass df-sk-bone--xxs" />
                        </div>
                      </div>
                      <span className="df-sk-glass df-sk-bone--cell" />
                      <span className="df-sk-glass df-sk-bone--cell" />
                      <span className="df-sk-glass df-sk-bone--cell df-sk-w50" />
                    </div>
                  ))}
                </div>
              ) : null}
              {!loading && filteredRows.length === 0 && !error ? (
                <div className="df-deposit-history-empty" aria-live="polite">
                  <img src="/images/no-data.svg" alt="" className="df-deposit-history-empty-img" width={120} height={120} />
                  <p className="df-deposit-history-empty-copy">No bank deposits yet. Transfer AED to your virtual IBAN.</p>
                </div>
              ) : null}
              {filteredRows.length > 0 ? (
                <>
                  <div className="df-deposit-history-desktop df-deposit-history-desktop--fiat">
                    <div className="df-deposit-history-head-wrap">
                      <div className="df-deposit-history-head" role="row">
                        {["Date", "Gross", "Fee", "Credited", "Status"].map((label) => (
                          <div key={label} className="df-deposit-history-head-cell" role="columnheader">
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="df-deposit-history-body-wrap" aria-label="Recent deposits">
                      {filteredRows.map((row) => (
                        <div key={row.id} className="df-deposit-history-row" role="row" {...bindRowOpen(row)}>
                          <div className="df-deposit-history-row-cell" title={row.dateLabel}>
                            {row.dateLabel}
                          </div>
                          <div className="df-deposit-history-row-cell">
                            {row.gross ? `${formatAedAmount(row.gross)} ${row.currency}` : "—"}
                          </div>
                          <div className="df-deposit-history-row-cell">
                            {formatAedAmount(row.fee)} {row.currency}
                            {row.hasFee && row.seqInDay ? (
                              <span className="df-deposit-history-reason">{ordinal(row.seqInDay)} deposit today</span>
                            ) : null}
                          </div>
                          <div className="df-deposit-history-row-cell">
                            {row.belowMinHold ? "—" : `${formatAedAmount(row.net)} ${row.currency}`}
                          </div>
                          <div className="df-deposit-history-row-cell df-deposit-history-row-cell--action">
                            <span className={`df-deposit-history-action-badge is-${row.statusTone}`}>
                              {row.statusLabel}
                            </span>
                            {row.statusReason ? (
                              <span className="df-deposit-history-reason">{row.statusReason}</span>
                            ) : null}
                            {row.belowMinHold ? (
                              <span className="df-deposit-history-reason">Wait for an admin refund. Do not retry KYC.</span>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="df-deposit-history-mobile" aria-label="Recent deposits">
                    {filteredRows.map((row) => (
                      <div key={row.id} className="df-deposit-history-mobile-card" {...bindRowOpen(row)}>
                        <div className="df-deposit-history-mobile-top">
                          <span className="df-deposit-history-mobile-date">{row.dateLabel}</span>
                          <span className={`df-deposit-history-action-badge is-${row.statusTone}`}>
                            {row.statusLabel}
                          </span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Gross</span>
                          <span className="df-deposit-history-mobile-value">
                            {row.gross ? `${formatAedAmount(row.gross)} ${row.currency}` : "—"}
                          </span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Fee</span>
                          <span className="df-deposit-history-mobile-value">
                            {formatAedAmount(row.fee)} {row.currency}
                            {row.hasFee && row.seqInDay ? ` · ${ordinal(row.seqInDay)} deposit today` : ""}
                          </span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Credited</span>
                          <span className="df-deposit-history-mobile-value">
                            {row.belowMinHold ? "—" : `${formatAedAmount(row.net)} ${row.currency}`}
                          </span>
                        </div>
                        {row.statusReason ? (
                          <div className="df-deposit-history-mobile-row">
                            <span className="df-deposit-history-mobile-label">Reason</span>
                            <span className="df-deposit-history-mobile-value">{row.statusReason}</span>
                          </div>
                        ) : null}
                        {row.belowMinHold ? (
                          <div className="df-deposit-history-mobile-row">
                            <span className="df-deposit-history-mobile-label">Next step</span>
                            <span className="df-deposit-history-mobile-value">Wait for an admin refund. Do not retry KYC.</span>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
              {hasMore && !error ? (
                <div className="df-deposit-history-more">
                  <button
                    type="button"
                    className="df-deposit-history-export-btn"
                    onClick={() => loadPage(page + 1, false)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <FiatDepositDetailModal open={detailOpen} row={detailRow} onClose={closeDetail} />
    </>
  );
}
